import React, { useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';

const MessageThread = ({ messages, familyMembers }) => {
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getMember = (senderId) => {
    return familyMembers?.find(m => m?.id === senderId);
  };

  const formatTime = (timestamp) => {
    try {
      return format(new Date(timestamp), 'h:mm a');
    } catch {
      return '';
    }
  };

  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday?.setDate(yesterday?.getDate() - 1);

      if (date?.toDateString() === today?.toDateString()) {
        return 'Today';
      } else if (date?.toDateString() === yesterday?.toDateString()) {
        return 'Yesterday';
      } else {
        return format(date, 'MMM d, yyyy');
      }
    } catch {
      return '';
    }
  };

  if (!messages || messages?.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-center">
        <div>
          <Icon name="message-square" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">
            No messages yet. Start the conversation!
          </p>
        </div>
      </div>
    );
  }

  // Group messages by date
  const groupedMessages = messages?.reduce((groups, message) => {
    const date = formatDate(message?.timestamp);
    if (!groups?.[date]) {
      groups[date] = [];
    }
    groups?.[date]?.push(message);
    return groups;
  }, {});

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {Object.entries(groupedMessages || {})?.map(([date, dateMessages]) => (
        <div key={date}>
          {/* Date separator */}
          <div className="flex items-center justify-center my-4">
            <div className="bg-muted px-3 py-1 rounded-full">
              <span className="text-xs font-medium text-muted-foreground">{date}</span>
            </div>
          </div>

          {/* Messages for this date */}
          {dateMessages?.map((message, index) => {
            const member = getMember(message?.senderId);
            const showAvatar = index === 0 || dateMessages?.[index - 1]?.senderId !== message?.senderId;

            return (
              <div key={message?.id} className="flex items-start gap-3 mb-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {showAvatar ? (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                      style={{ backgroundColor: member?.color || '#6B7280' }}
                    >
                      {member?.initials || '??'}
                    </div>
                  ) : (
                    <div className="w-8 h-8" />
                  )}
                </div>

                {/* Message content */}
                <div className="flex-1 min-w-0">
                  {showAvatar && (
                    <div className="flex items-baseline gap-2 mb-1">
                      <span
                        className="font-semibold text-sm"
                        style={{ color: member?.color || '#6B7280' }}
                      >
                        {member?.name || 'Unknown'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatTime(message?.timestamp)}
                      </span>
                    </div>
                  )}
                  
                  <div className="bg-muted rounded-lg px-3 py-2 inline-block max-w-full">
                    <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                      {message?.content}
                    </p>
                  </div>

                  {/* Read receipt */}
                  {message?.read && (
                    <div className="flex items-center gap-1 mt-1">
                      <Icon name="check-check" size={12} className="text-primary" />
                      <span className="text-xs text-muted-foreground">Read</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageThread;